import { Link, useParams } from 'react-router-dom';
import { Plus, ArrowLeft } from 'lucide-react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { ProductList } from '@/components/products';
import { useProducerProducts, ProducerProduct } from '@/hooks/useProducerProducts';
import { useProducerUsage } from '@/hooks/useProducerUsage';
import { SoftLimitMessage } from '@/components/producer/SoftLimitMessage';
import { useToast } from '@/hooks/use-toast';

export default function AdminProductList() {
  const { producerId } = useParams<{ producerId: string }>();
  const { products, loading, error, deleteProduct, updateProduct } = useProducerProducts(producerId);
  const { data: usage } = useProducerUsage(producerId);
  const { toast } = useToast();

  const limitReached = usage.products_limit !== null && usage.products_used >= usage.products_limit;

  const basePath = `/admin/producers/${producerId}`;

  const handleDeleteProduct = async (product: ProducerProduct) => {
    try {
      await deleteProduct(product.id);
      toast({
        title: 'Produkt borttagen',
        description: `${product.title} har tagits bort.`,
      });
    } catch (err) {
      toast({
        title: 'Kunde inte ta bort',
        description: 'Något gick fel. Försök igen.',
        variant: 'destructive',
      });
    }
  };

  const handleToggleStatus = async (product: ProducerProduct) => {
    const newStatus = product.status === 'public' ? 'draft' : 'public';
    const description = newStatus === 'public'
      ? 'Produkten är nu synlig i din gårdsbutik.'
      : 'Produkten är inte längre synlig i din gårdsbutik.';
    try {
      await updateProduct({ id: product.id, status: newStatus });
      toast({ title: newStatus === 'public' ? 'Publicerad' : 'Avpublicerad', description });
    } catch (err) {
      toast({
        title: 'Kunde inte ändra status',
        description: 'Något gick fel. Försök igen.',
        variant: 'destructive',
      });
    }
  };

  return (
    <AdminLayout>
      <div className="w-full max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <Button variant="ghost" size="sm" asChild className="mb-4 -ml-2">
            <Link to={`/admin/producers/${producerId}`}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Tillbaka till producent
            </Link>
          </Button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Produkter</h1>
              <p className="text-muted-foreground mt-1">
                Hantera produkter i din gårdsbutik
              </p>
            </div>
            <Button asChild={!limitReached} disabled={limitReached}>
              {limitReached ? (
                <span>
                  <Plus className="h-4 w-4 mr-2" />
                  Skapa produkt
                </span>
              ) : (
                <Link to={`${basePath}/products/new`}>
                  <Plus className="h-4 w-4 mr-2" />
                  Skapa produkt
                </Link>
              )}
            </Button>
          </div>

          {/* Soft limit message */}
          <div className="mt-3">
            <SoftLimitMessage
              used={usage.products_used}
              limit={usage.products_limit}
              resourceName="produkter"
              extraInfo={limitReached ? 'Uppgradera din plan för att skapa fler.' : undefined}
            />
          </div>
        </div>

        {/* Product list using shared component */}
        <ProductList
          products={products}
          loading={loading}
          error={error}
          onDeleteProduct={handleDeleteProduct}
          onToggleStatus={handleToggleStatus}
          basePath={basePath}
        />
      </div>
    </AdminLayout>
  );
}
